import {ChangeDetectionStrategy, Component, computed, ElementRef, Injectable, input, OnInit, output, viewChild} from "@angular/core";
import {CriiptoResponsStatus, DokumentVersjonModell, Signeringsmetode} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {lyttTilCriiptoSignertHendelser} from "felles-ng2/støttefunksjoner/criipto";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {TranslateModule} from "@ngx-translate/core";
import {RessursAdresser} from "felles-ng2/tjenester/ressurser/RessursAdresser";
import {DokumentSigneringDatatjeneste} from "felles-ng2/tjenester/datalager/DokumentSigneringDatatjeneste";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";

const debug = initDebugger('SigneringCriiptoKomponent');


/**
 * Signering av dokument vhja criipto,
 * laster criipto i en iframe.
 */
@Component({
    selector: 'signering-criipto',
    templateUrl: 'signering-criipto.html',
    styleUrl: 'signering-criipto.scss',
    host: {
        '[class]': '"metode-"+metode()'
    },
    imports: [
        TranslateModule
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SigneringCriiptoKomponent implements OnInit {

    iframe = viewChild.required<ElementRef<HTMLIFrameElement>>('iframe');

    vedSignert = output();
    vedAvbryt = output();

    dokumentversjon = input.required<DokumentVersjonModell>();
    metode = input.required<Signeringsmetode>();

    erSvenskBankId = computed(() => [Signeringsmetode.svensk_bankid_samedevice, Signeringsmetode.svensk_bankid_anotherdevice, Signeringsmetode.svensk_bankid_anotherdeviceqr]
      .includes(this.metode()))

    constructor(private avhengigheter: SigneringCriiptoKomponentAvhengigheter,
                private oversettelser: Oversettelser,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private ressursAdresser: RessursAdresser) {
        lyttTilCriiptoSignertHendelser().pipe(takeUntilDestroyed())
          .subscribe(event => this.signertIIframeCallback(event.data.status, event.data.error));
    }

    ngOnInit(): void {
        this.lastCriiptoIframe();
    }

    // hjelpemetoder

    private signertIIframeCallback(status: CriiptoResponsStatus, error: string) {
        debug(`signertIIframeCallback med status:${status}, error:${error}`);

        switch (status) {
            case CriiptoResponsStatus.ok:
                return this.signeringOK();
            case CriiptoResponsStatus.avbrutt_av_bruker:
                return this.skjulDialogOgAvbryt();
            default:
                return this.signeringFeilet(error);
        }
    }

    private skjulDialogOgAvbryt() {
        this.vedAvbryt.emit();
    }

    private signeringFeilet(err: any) {
        const melding = err ?
          this.oversettelser.oversett(i18n('En feil oppstod ved signering. Feilkode {feilkode}'), {feilkode: err}) :
          this.oversettelser.oversett(i18n('En feil oppstod ved signering.'));
        this.meldingTilBruker.feilmelding(melding);
        this.skjulDialogOgAvbryt();
    }

    private signeringOK() {
        this.avhengigheter.oppdaterSigneringsstatus(this.dokumentversjon());
        this.vedSignert.emit();
    }

    private lastCriiptoIframe() {
        //for å unngå potensielt problem med popup-blokker i safari lar vi her server re-dirigere til korrekt url,
        // fremfor å hente url som promise og så åpne vindu når promise resolver - som kan føre til popup-blokkering
        const url = this.ressursAdresser.criiptoSigneringUrl(this.dokumentversjon().dokumentId, this.metode())
        debug(`lastCriiptoIframe med url: ${url}`)
        this.iframe().nativeElement.src = url;
    }

}


@Injectable()
export class SigneringCriiptoKomponentAvhengigheter {

    constructor(private dokumentSigneringDatatjeneste: DokumentSigneringDatatjeneste, private modelloppdateringer: Modelloppdateringer) {
    }

    oppdaterSigneringsstatus(dokument: DokumentVersjonModell) {
        //@todo denne utfases når dokumentDataLager er tatt i bruk på sak..
        this.modelloppdateringer.broadcastLokalSignatarSignert(dokument.dokumentId);
        this.dokumentSigneringDatatjeneste.oppdaterSigneringsstatusILokalLager(dokument);
    }
}
